import { RubyLoader } from '../components/RubyLoader';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../services/apiService';
import { Tenant } from '../types';
import { PaymentMethodModal } from '../components/StripePaymentMethodForm';
import {
  CreditCard,
  AlertCircle,
  CheckCircle,
  Download,
  Eye,
  EyeOff,
} from 'lucide-react';

interface UpcomingInvoiceData {
  date?: string;
  amount?: number;
  amount_due?: number;
  description?: string;
  items?: Array<{ description: string; amount: number }>;
  currency?: string;
  next_payment_attempt?: number;
  lines?: { data: any[] };
}

interface PaymentHistoryItem {
  id?: string;
  tenantId?: string;
  eventTimestamp?: number;
  type?: string;
  amount?: number;
  amountPaid?: number;
  amountDue?: number;
  currency?: string;
  status?: string;
  description?: string;
  invoiceUrl?: string;
}

const TenantBilling: React.FC = () => {
  const { tenantId } = useParams<{ tenantId: string }>();
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [paymentHistory, setPaymentHistory] = useState<PaymentHistoryItem[]>([]);
  const [upcomingInvoice, setUpcomingInvoice] = useState<UpcomingInvoiceData | null>(null);
  const [showCardDetails, setShowCardDetails] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'payment' | 'history' | 'support'>('overview');
  const [updating, setUpdating] = useState(false);
  const [startingCheckout, setStartingCheckout] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  useEffect(() => {
    loadTenantData();
  }, [tenantId]);

  const loadTenantData = async () => {
    try {
      setLoading(true);
      if (!tenantId) return;
      console.log('Loading billing data for tenantId:', tenantId);
      const [tenantData, billingData] = await Promise.all([
        api.superAdmin.getTenant(tenantId),
        api.superAdmin.getTenantBilling(tenantId),
      ]);
      console.log('Tenant data received:', tenantData);
      console.log('Stripe subscription ID:', tenantData.stripeSubscriptionId);
      setTenant(tenantData);
      
      // Only show real payment history if subscription exists
      if (tenantData.stripeSubscriptionId) {
        setPaymentHistory(billingData.events || []);
        
        // Get upcoming invoice
        try {
          const upcomingData = await api.superAdmin.upcomingInvoice(tenantData.stripeSubscriptionId);
          setUpcomingInvoice(upcomingData);
        } catch (err) {
          console.warn('No upcoming invoice', err);
        }
      } else {
        // No subscription - clear any mock data
        setPaymentHistory([]);
        setUpcomingInvoice(null);
      }
    } catch (error) {
      console.error('Failed to load billing data', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'past_due':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'suspended':
        return 'bg-red-100 text-red-800 border-red-300';
      case 'cancelled':
        return 'bg-gray-100 text-gray-800 border-gray-300';
      default:
        return 'bg-blue-100 text-blue-800 border-blue-300';
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { icon: React.ReactNode; label: string; color: string }> = {
      active: {
        icon: <CheckCircle className="w-5 h-5" />,
        label: 'Active',
        color: '#16a34a',
      },
      past_due: {
        icon: <AlertCircle className="w-5 h-5" />,
        label: 'Past Due',
        color: '#ea580c',
      },
      suspended: {
        icon: <AlertCircle className="w-5 h-5" />,
        label: 'Suspended',
        color: '#dc2626',
      },
      cancelled: {
        icon: <AlertCircle className="w-5 h-5" />,
        label: 'Cancelled',
        color: '#666',
      },
    };

    const config = statusConfig[status] || statusConfig.active;
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: config.color }}>
        {config.icon}
        <span style={{ fontWeight: '600', fontSize: '14px' }}>{config.label}</span>
      </div>
    );
  };

  const getPaymentStatusColor = (status: string) => {
    switch (status) {
      case 'paid':
        return '#16a34a';
      case 'failed':
        return '#dc2626';
      case 'pending':
        return '#ea580c';
      case 'refunded':
        return '#0284c7';
      default:
        return '#666';
    }
  };

  const handleUpdatePayment = () => {
    // Open Stripe payment modal
    setShowPaymentModal(true);
  };

  const handlePaymentMethodUpdated = async (paymentMethodId: string) => {
    if (!tenant?.stripeSubscriptionId) return;
    try {
      setUpdating(true);
      await api.superAdmin.updatePaymentMethod(tenant.stripeSubscriptionId, paymentMethodId);
      setShowPaymentModal(false);
      await loadTenantData();
    } catch (error) {
      console.error('Failed to update payment method', error);
      alert('Failed to update payment method. Please try again.');
    } finally {
      setUpdating(false);
    }
  };

  const handleStartSubscription = async () => {
    if (!tenantId) return;
    try {
      setStartingCheckout(true);
      const origin = window.location.origin;
      const resp = await api.superAdmin.createTenantCheckoutSession({
        tenantId,
        successUrl: `${origin}/#/billing?subscription=success`,
        cancelUrl: `${origin}/#/billing?subscription=cancel`
      });
      if (resp?.url) {
        window.location.href = resp.url;
        return;
      }
      throw new Error('No checkout URL returned');
    } catch (error: any) {
      console.error('Failed to start subscription checkout', error);
      alert(error?.message || 'Unable to start subscription. Please try again.');
    } finally {
      setStartingCheckout(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!tenant?.stripeSubscriptionId) {
      alert('No subscription ID found');
      return;
    }
    try {
      setUpdating(true);
      // Check if already scheduled for cancellation
      const isCancelling = tenant.cancelAtPeriodEnd === true;
      
      console.log('Toggling cancellation:', { 
        subscriptionId: tenant.stripeSubscriptionId, 
        currentlyCancelling: isCancelling,
        willCancel: !isCancelling 
      });
      
      if (isCancelling) {
        // Resume subscription (remove cancellation)
        const result = await api.superAdmin.setCancelAtPeriodEnd(tenant.stripeSubscriptionId, false);
        console.log('Resume result:', result);
      } else {
        // Schedule cancellation at period end
        const result = await api.superAdmin.setCancelAtPeriodEnd(tenant.stripeSubscriptionId, true);
        console.log('Cancel result:', result);
      }
      
      setShowCancelModal(false);
      await loadTenantData();
    } catch (error: any) {
      console.error('Failed to update subscription cancellation', error);
      alert(`Failed to ${tenant.cancelAtPeriodEnd ? 'resume' : 'cancel'} subscription: ${error.message || 'Unknown error'}`);
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <RubyLoader variant="page" label="Loading billing information..." />
    );
  }

  if (!tenant) {
    return (
      <div style={{ padding: '40px 20px', textAlign: 'center', color: '#666' }}>
        <p>Billing information not available</p>
      </div>
    );
  }

  // Show setup prompt if no subscription
  if (!tenant.stripeSubscriptionId) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#0f0b0a', color: '#f5f5f4', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '32px' }}>
        <div style={{ width: '100%', maxWidth: '640px', background: 'linear-gradient(135deg, rgba(255,184,96,0.08), rgba(255,255,255,0.02))', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '24px', padding: '32px', boxShadow: '0 20px 50px rgba(0,0,0,0.4)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
            <div style={{ width: '56px', height: '56px', borderRadius: '16px', background: 'linear-gradient(135deg, #d97706, #b45309)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 10px 25px rgba(217,119,6,0.35)' }}>
              <span role="img" aria-label="lock" style={{ fontSize: '28px' }}>🔒</span>
            </div>
            <div>
              <p style={{ fontSize: '12px', letterSpacing: '0.1em', color: '#d6d3d1', margin: 0 }}>SUBSCRIPTION NEEDED</p>
              <h2 style={{ fontSize: '26px', fontWeight: 800, margin: '4px 0 0', color: '#fef3c7' }}>Activate your CafeMaster Pro access</h2>
            </div>
          </div>

          <p style={{ color: '#e7e5e4', fontSize: '15px', lineHeight: 1.7, marginBottom: '20px' }}>
            Your account is almost ready. Add your payment method to start your subscription and unlock all features. You can cancel anytime.
          </p>

          <div style={{ backgroundColor: '#1c1917', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '16px', padding: '16px', marginBottom: '20px' }}>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: '10px' }}>
              <li style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#d6d3d1', fontSize: '14px' }}>
                <span style={{ color: '#22c55e' }}>✓</span> Secure Stripe checkout
              </li>
              <li style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#d6d3d1', fontSize: '14px' }}>
                <span style={{ color: '#22c55e' }}>✓</span> Instant activation
              </li>
              <li style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#d6d3d1', fontSize: '14px' }}>
                <span style={{ color: '#22c55e' }}>✓</span> Cancel anytime
              </li>
            </ul>
          </div>

          <button
            onClick={handleStartSubscription}
            disabled={startingCheckout}
            style={{
              width: '100%',
              padding: '16px',
              borderRadius: '14px',
              border: 'none',
              background: startingCheckout ? '#78350f' : 'linear-gradient(135deg, #ea580c, #d97706)',
              color: '#fff7ed',
              fontWeight: 700,
              fontSize: '16px',
              letterSpacing: '0.03em',
              cursor: startingCheckout ? 'not-allowed' : 'pointer',
              boxShadow: '0 15px 30px rgba(234,88,12,0.35)',
              transition: 'transform 120ms ease, box-shadow 120ms ease'
            }}
            onMouseEnter={(e) => { if (!startingCheckout) e.currentTarget.style.transform = 'translateY(-1px)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
          >
            {startingCheckout ? 'Starting checkout…' : 'Start subscription'}
          </button>

          <p style={{ textAlign: 'center', color: '#a8a29e', fontSize: '13px', marginTop: '14px' }}>
            Need help? Email <a href="mailto:support@cafemaster.com" style={{ color: '#f59e0b', textDecoration: 'none' }}>support@cafemaster.com</a>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto', padding: '20px' }}>
      {/* Header */}
      <div style={{ marginBottom: '30px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: '#1f1f1f', marginBottom: '10px' }}>
          💳 Billing & Subscription
        </h1>
        <p style={{ color: '#666' }}>Manage your subscription, payment methods, and billing history</p>
      </div>

      {/* Subscription Status Card */}
      {tenant.subscriptionStatus === 'past_due' && (
        <div
          style={{
            backgroundColor: '#fef3c7',
            border: '2px solid #ea580c',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '24px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div>
            <h3 style={{ color: '#713f12', fontWeight: '600', marginBottom: '4px' }}>
              ⚠️ Payment Action Required
            </h3>
            <p style={{ color: '#713f12', fontSize: '14px' }}>
              Your payment failed. Please update your payment method to avoid service interruption.
            </p>
          </div>
          <button
            onClick={handleUpdatePayment}
            style={{
              backgroundColor: '#dc2626',
              color: 'white',
              padding: '10px 20px',
              borderRadius: '6px',
              border: 'none',
              fontWeight: '600',
              cursor: 'pointer',
            }}
          >
            Update Now
          </button>
        </div>
      )}

      {/* Main Content Tabs */}
      <div
        style={{
          display: 'flex',
          gap: '12px',
          borderBottom: '2px solid #e5e7eb',
          marginBottom: '24px',
        }}
      >
        {(['overview', 'payment', 'history', 'support'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              padding: '12px 16px',
              border: 'none',
              background: activeTab === tab ? '#d97706' : 'transparent',
              color: activeTab === tab ? 'white' : '#666',
              fontWeight: activeTab === tab ? '600' : '500',
              cursor: 'pointer',
              borderRadius: '4px 4px 0 0',
              fontSize: '14px',
            }}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div>
          {/* Subscription Status Card */}
          <div
            style={{
              backgroundColor: '#ffffff',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              padding: '20px',
              marginBottom: '20px',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '16px' }}>
              <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#1f1f1f' }}>Subscription Status</h2>
              {getStatusBadge(tenant.subscriptionStatus || 'active')}
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '16px',
                marginBottom: '20px',
              }}
            >
              <div>
                <p style={{ fontSize: '12px', fontWeight: '600', color: '#61381b', marginBottom: '6px' }}>
                  Plan Name
                </p>
                <p style={{ fontSize: '16px', color: '#1f1f1f', fontWeight: '500' }}>
                  Professional Plan
                </p>
              </div>
              <div>
                <p style={{ fontSize: '12px', fontWeight: '600', color: '#61381b', marginBottom: '6px' }}>
                  Monthly Cost
                </p>
                <p style={{ fontSize: '16px', color: '#1f1f1f', fontWeight: '500' }}>
                  ${((tenant.subscriptionCost || 0) / 100).toFixed(2)}/month
                </p>
              </div>
              <div>
                <p style={{ fontSize: '12px', fontWeight: '600', color: '#61381b', marginBottom: '6px' }}>
                  Next Billing Date
                </p>
                <p style={{ fontSize: '16px', color: '#1f1f1f', fontWeight: '500' }}>
                  {tenant.currentPeriodEnd
                    ? new Date(tenant.currentPeriodEnd).toLocaleDateString()
                    : 'Not set'}
                </p>
              </div>
              <div>
                <p style={{ fontSize: '12px', fontWeight: '600', color: '#61381b', marginBottom: '6px' }}>
                  Subscription ID
                </p>
                <p style={{ fontSize: '13px', color: '#666', fontFamily: 'monospace' }}>
                  {tenant.stripeSubscriptionId || 'N/A'}
                </p>
              </div>
            </div>

            {tenant.cancelAtPeriodEnd && (
              <div style={{ backgroundColor: '#fef3c7', borderRadius: '6px', padding: '12px', marginBottom: '16px', border: '1px solid #fbbf24' }}>
                <p style={{ color: '#92400e', fontSize: '14px', fontWeight: '600' }}>
                  ⚠️ Subscription scheduled to cancel on {tenant.currentPeriodEnd ? new Date(tenant.currentPeriodEnd).toLocaleDateString() : 'end of period'}
                </p>
              </div>
            )}

            <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '16px', display: 'flex', gap: '12px' }}>
              <button
                onClick={handleUpdatePayment}
                style={{
                  backgroundColor: '#d97706',
                  color: 'white',
                  padding: '10px 20px',
                  borderRadius: '6px',
                  border: 'none',
                  fontWeight: '600',
                  cursor: 'pointer',
                  fontSize: '14px',
                }}
              >
                Update Payment Method
              </button>
              <button
                onClick={() => setShowCancelModal(true)}
                style={{
                  backgroundColor: tenant.cancelAtPeriodEnd ? '#10b981' : '#f3f4f6',
                  color: tenant.cancelAtPeriodEnd ? 'white' : '#666',
                  padding: '10px 20px',
                  borderRadius: '6px',
                  border: tenant.cancelAtPeriodEnd ? 'none' : '1px solid #e5e7eb',
                  fontWeight: '600',
                  cursor: 'pointer',
                  fontSize: '14px',
                }}
              >
                {tenant.cancelAtPeriodEnd ? 'Resume Subscription' : 'Cancel Subscription'}
              </button>
            </div>
          </div>

          {/* Upcoming Invoice */}
          {upcomingInvoice && (
            <div
              style={{
                backgroundColor: '#ffffff',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                padding: '20px',
              }}
            >
              <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#1f1f1f', marginBottom: '16px' }}>
                📊 Upcoming Invoice
              </h2>
              <div
                style={{
                  backgroundColor: '#f9fafb',
                  borderRadius: '6px',
                  padding: '16px',
                  marginBottom: '16px',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <span style={{ color: '#666', fontWeight: '600' }}>Invoice Date:</span>
                  <span style={{ color: '#1f1f1f', fontWeight: '500' }}>{upcomingInvoice.date || 'TBD'}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #e5e7eb', paddingTop: '12px' }}>
                  <span style={{ color: '#1f1f1f', fontWeight: '600', fontSize: '16px' }}>Total:</span>
                  <span style={{ color: '#d97706', fontWeight: 'bold', fontSize: '18px' }}>
                    ${(upcomingInvoice.amount || 0).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Payment Method Tab */}
      {activeTab === 'payment' && (
        <div
          style={{
            backgroundColor: '#ffffff',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            padding: '20px',
          }}
        >
          <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#1f1f1f', marginBottom: '20px' }}>
            Payment Method
          </h2>

          {/* Card Display */}
          <div
            style={{
              backgroundColor: '#f3f4f6',
              border: '2px solid #d1d5db',
              borderRadius: '8px',
              padding: '20px',
              marginBottom: '20px',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <CreditCard style={{ width: '24px', height: '24px', color: '#d97706' }} />
                <span style={{ fontWeight: '600', color: '#1f1f1f' }}>Credit Card</span>
              </div>
              <button
                onClick={() => setShowCardDetails(!showCardDetails)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#d97706',
                  fontWeight: '600',
                }}
              >
                {showCardDetails ? (
                  <>
                    <EyeOff style={{ width: '18px', height: '18px', display: 'inline', marginRight: '4px' }} />
                    Hide
                  </>
                ) : (
                  <>
                    <Eye style={{ width: '18px', height: '18px', display: 'inline', marginRight: '4px' }} />
                    Show
                  </>
                )}
              </button>
            </div>

            {showCardDetails ? (
              <div style={{ fontSize: '14px', color: '#666' }}>
                <p style={{ marginBottom: '8px' }}>
                  <strong>Card Type:</strong> Visa
                </p>
                <p style={{ marginBottom: '8px' }}>
                  <strong>Card Number:</strong> •••• •••• •••• 4242
                </p>
                <p>
                  <strong>Expires:</strong> 12/26
                </p>
              </div>
            ) : (
              <p style={{ color: '#666', fontSize: '14px' }}>
                •••• •••• •••• 4242 - Expires 12/26
              </p>
            )}
          </div>

          {/* Update Button */}
          <button
            onClick={handleUpdatePayment}
            disabled={updating}
            style={{
              backgroundColor: '#d97706',
              color: 'white',
              padding: '12px 24px',
              borderRadius: '6px',
              border: 'none',
              fontWeight: '600',
              cursor: updating ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              opacity: updating ? 0.6 : 1,
            }}
          >
            {updating ? 'Updating...' : 'Update Payment Method'}
          </button>

          <p style={{ marginTop: '16px', fontSize: '12px', color: '#666' }}>
            🔒 Your payment information is secure and encrypted with Stripe's industry-leading security.
          </p>
        </div>
      )}

      {/* Billing History Tab */}
      {activeTab === 'history' && (
        <div
          style={{
            backgroundColor: '#ffffff',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            overflow: 'hidden',
          }}
        >
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
            }}
          >
            <thead>
              <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#61381b' }}>
                  Date
                </th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#61381b' }}>
                  Description
                </th>
                <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '600', color: '#61381b' }}>
                  Amount
                </th>
                <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: '600', color: '#61381b' }}>
                  Status
                </th>
                <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: '600', color: '#61381b' }}>
                  Invoice
                </th>
              </tr>
            </thead>
            <tbody>
              {paymentHistory.map((payment, index) => (
                <tr key={payment.id || index} style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <td style={{ padding: '12px 16px', color: '#666', fontSize: '14px' }}>
                    {new Date(payment.eventTimestamp || Date.now()).toLocaleDateString()}
                  </td>
                  <td style={{ padding: '12px 16px', color: '#1f1f1f', fontSize: '14px' }}>
                    {payment.description || payment.type || 'Payment'}
                  </td>
                  <td
                    style={{
                      padding: '12px 16px',
                      textAlign: 'right',
                      color: '#1f1f1f',
                      fontWeight: '600',
                      fontSize: '14px',
                    }}
                  >
                    ${payment.amount ? payment.amount.toFixed(2) : '0.00'}
                  </td>
                  <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                    <span
                      style={{
                        display: 'inline-block',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: '600',
                        backgroundColor:
                          payment.status === 'paid'
                            ? '#dcfce7'
                            : payment.status === 'failed'
                              ? '#fee2e2'
                              : payment.status === 'refunded'
                                ? '#e0f2fe'
                                : '#fef3c7',
                        color: getPaymentStatusColor(payment.status || 'unknown'),
                      }}
                    >
                      {payment.status ? payment.status.charAt(0).toUpperCase() + payment.status.slice(1) : 'Unknown'}
                    </span>
                  </td>
                  <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                    {payment.invoiceUrl ? (
                      <a
                        href={payment.invoiceUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: '#d97706',
                          textDecoration: 'none',
                          fontWeight: '600',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                        }}
                      >
                        <Download style={{ width: '16px', height: '16px' }} />
                        Download
                      </a>
                    ) : (
                      <span style={{ color: '#999', fontSize: '12px' }}>—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Support Tab */}
      {activeTab === 'support' && (
        <div
          style={{
            backgroundColor: '#ffffff',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            padding: '20px',
          }}
        >
          <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#1f1f1f', marginBottom: '16px' }}>
            💬 Billing Support
          </h2>

          <div style={{ backgroundColor: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '6px', padding: '16px', marginBottom: '16px' }}>
            <h3 style={{ color: '#0c4a6e', fontWeight: '600', marginBottom: '8px' }}>
              Questions About Your Billing?
            </h3>
            <p style={{ color: '#0c4a6e', fontSize: '14px', marginBottom: '12px' }}>
              Our support team is here to help with any billing questions or concerns.
            </p>
            <a
              href="mailto:support@cafemasterpro.com"
              style={{
                display: 'inline-block',
                backgroundColor: '#0284c7',
                color: 'white',
                padding: '10px 20px',
                borderRadius: '6px',
                textDecoration: 'none',
                fontWeight: '600',
                fontSize: '14px',
              }}
            >
              Contact Support
            </a>
          </div>

          <div style={{ marginTop: '20px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1f1f1f', marginBottom: '12px' }}>
              Common Questions
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {[
                { q: 'When will I be charged?', a: 'You will be charged on the day of each billing cycle.' },
                { q: 'Can I change my plan?', a: 'Yes, you can upgrade or downgrade your plan anytime.' },
                { q: 'How do I get an invoice?', a: 'Invoices are available in the Billing History tab above.' },
              ].map((faq, idx) => (
                <div key={idx} style={{ backgroundColor: '#f9fafb', padding: '12px', borderRadius: '6px' }}>
                  <p style={{ fontWeight: '600', color: '#1f1f1f', marginBottom: '4px' }}>Q: {faq.q}</p>
                  <p style={{ color: '#666', fontSize: '14px' }}>A: {faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Cancel Subscription Modal */}
      {showCancelModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '30px',
              maxWidth: '500px',
              width: '90%',
            }}
          >
            <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: '#1f1f1f', marginBottom: '12px' }}>
              {tenant.cancelAtPeriodEnd ? 'Resume Subscription?' : 'Cancel Subscription?'}
            </h2>
            <p style={{ color: '#666', marginBottom: '20px' }}>
              {tenant.cancelAtPeriodEnd 
                ? 'Your subscription is scheduled to cancel. Would you like to resume and continue service beyond the current period?'
                : 'Are you sure you want to cancel your subscription? Your service will continue until the end of the current billing period.'}
            </p>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => setShowCancelModal(false)}
                style={{
                  flex: 1,
                  padding: '10px',
                  backgroundColor: '#f3f4f6',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontWeight: '600',
                  cursor: 'pointer',
                }}
              >
                {tenant.cancelAtPeriodEnd ? 'Keep Cancellation' : 'Keep Subscription'}
              </button>
              <button
                onClick={handleCancelSubscription}
                disabled={updating}
                style={{
                  flex: 1,
                  padding: '10px',
                  backgroundColor: tenant.cancelAtPeriodEnd ? '#10b981' : '#dc2626',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontWeight: '600',
                  cursor: updating ? 'not-allowed' : 'pointer',
                  opacity: updating ? 0.6 : 1,
                }}
              >
                {updating ? 'Updating...' : (tenant.cancelAtPeriodEnd ? 'Resume Subscription' : 'Schedule Cancellation')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stripe Payment Method Modal */}
      {showPaymentModal && tenantId && (
        <PaymentMethodModal
          isOpen={showPaymentModal}
          tenantId={tenantId}
          onClose={() => setShowPaymentModal(false)}
          onSuccess={handlePaymentMethodUpdated}
        />
      )}
    </div>
  );
};

export default TenantBilling;
